import { IConnectRender } from "../../../../definitions/IConnectRender";
import { OlvidePasswordCardRender } from "./OlvidePasswordCardRender";
import { RestablecerPasswordCardRender } from "./RestablecerPasswordCardRender";
import { VerificarCodigoCardRender } from "./VerificarCodigoCardRender";

let FLOW_ID = 0;

export type RecuperarPasswordConfig = {
  /** Email precargado (el que el usuario venía escribiendo en el ingreso). */
  email: string;
  /** El usuario abandonó el flujo: hay que volver al formulario de ingreso. */
  onCancel: () => void;
  /** Contraseña cambiada: volver al ingreso con el email usado. */
  onRestablecido: (email: string) => void;
};

/** Flujo "olvidé mi contraseña" (3 pasos) dentro de la pantalla de login: no
 *  hay rutas nuevas ni deep links. Es el dueño del estado —email, `resetToken`
 *  y su expiración— que vive **solo en memoria**: cerrar o cancelar el flujo lo
 *  pierde. Cada paso es una card independiente que solo recibe callbacks. */
export class RecuperarPasswordFlowRender implements IConnectRender {
  private readonly _config: RecuperarPasswordConfig;
  private readonly _wrapperId: string;

  constructor(config: RecuperarPasswordConfig) {
    FLOW_ID += 1;
    this._config = config;
    this._wrapperId = `recuperarPassword-${FLOW_ID}`;
    this._email = config.email;
  }

  render(): string {
    return `<div id="${this._wrapperId}"></div>`;
  }

  connect(): void {
    this._wrapper = document.getElementById(this._wrapperId);
    this._showPaso1();
  }

  dispose(): void {
    this._current?.dispose();
    this._current = null;
    this._wrapper = null;
  }

  private _wrapper: HTMLElement | null = null;
  private _current: IConnectRender | null = null;
  /** Email en curso (el del paso 1 pisa al precargado). */
  private _email: string;
  /** Token del paso 2 — en memoria, nunca en storage. */
  private _resetToken: string = "";
  /** Expiración del token, en milisegundos Unix. */
  private _expiraEn: number = 0;

  /** Paso 1: pedir el código al email. */
  private _showPaso1(): void {
    this._mount(
      new OlvidePasswordCardRender({
        email: this._email,
        onCodigoEnviado: (email) => {
          this._email = email;
          this._showPaso2();
        },
        onCancel: () => this._config.onCancel(),
      }),
    );
  }

  /** Paso 2: canjear el código por el `resetToken`. */
  private _showPaso2(): void {
    this._mount(
      new VerificarCodigoCardRender({
        email: this._email,
        onVerificado: (resetToken, expiraEn) => {
          this._resetToken = resetToken;
          this._expiraEn = expiraEn;
          this._showPaso3();
        },
        onVolverAlPaso1: () => this._showPaso1(),
      }),
    );
  }

  /** Paso 3: definir la contraseña nueva. */
  private _showPaso3(): void {
    this._mount(
      new RestablecerPasswordCardRender({
        resetToken: this._resetToken,
        expiraEn: this._expiraEn,
        onRestablecido: () => this._config.onRestablecido(this._email),
        onTokenInvalido: () => {
          this._resetToken = "";
          this._expiraEn = 0;
          this._showPaso1();
        },
        onCancel: () => this._config.onCancel(),
      }),
    );
  }

  /** Reemplaza la card visible, disponiendo la anterior antes de sacarla del DOM. */
  private _mount(card: IConnectRender): void {
    if (!this._wrapper) return;

    this._current?.dispose();
    this._current = card;
    this._wrapper.innerHTML = card.render();
    card.connect();
  }
}
